import { Link } from 'react-router-dom'
import { ArrowRight, Home as HomeIcon, Palmtree, KeyRound, Phone } from 'lucide-react'
import Section from '../components/layout/Section'
import Reveal from '../components/layout/Reveal'
import Seo from '../components/seo/Seo'
import { useBreadcrumbs } from '../seo/useBreadcrumbs'
import { serviceSchema, faqSchema } from '../seo/schema'
import { AREAS } from '../data/areas'

const BUYER_FAQ = [
  {
    question: 'What are the tax advantages of buying in Delaware?',
    answer:
      'Delaware has no sales tax and no tax on Social Security benefits, and Sussex County property taxes are among the lowest in the Mid-Atlantic region. The main transaction cost is the realty transfer tax — 4% of the price, customarily split between buyer and seller. Verify current rates and your own tax situation with your accountant; the structural advantages are well established.',
  },
  {
    question: 'Can I use a second home at the beach as a rental when I\'m not there?',
    answer:
      'Often yes — the Delaware beaches have a mature summer weekly-rental market — but the rules are address-specific. Each town licenses and regulates rentals differently, and many HOAs restrict or prohibit short-term rentals. Tell us rental income matters up front and we\'ll only show you properties where it\'s allowed, with actual rental comps where available.',
  },
  {
    question: 'How is financing a second home different from a primary residence?',
    answer:
      'Second-home loans typically require larger down payments and slightly higher rates than primary-residence loans, and "investment property" loans are a different category again with stricter terms. How you\'ll actually use the property determines which applies — we work with local lenders who understand beach purchases and can make introductions.',
  },
  {
    question: 'Do I need my own agent for new construction at the beach?',
    answer:
      'Yes — the builder\'s sales rep works for the builder. Having your own representation costs you nothing (the builder pays the commission) and gets you contract review, inspection advocacy, and honest advice on lot premiums, options pricing, and community rules. Shawn\'s construction background is especially valuable when evaluating build quality.',
  },
  {
    question: 'What does flood insurance cost at the Delaware beaches?',
    answer:
      'It varies enormously by flood zone, elevation, and building type — from modest premiums for elevated or X-zone homes to significant costs for older slab-on-grade properties in AE zones. We pull flood zone determinations and actual insurance quotes during due diligence on every beach property, so costs are known before you\'re committed.',
  },
]

export default function Buy() {
  const crumbs = useBreadcrumbs([{ label: 'Buy', path: '/buy' }])
  return (
    <div className="bg-neutral-charcoal">
      <Seo
        title="Buy a Home at the Delaware Beaches | Buyer's Guide — Lewes to Fenwick"
        description="Buying a beach house in Delaware? Second-home, primary, or investment — local guidance on towns, taxes, rentals, and flood zones from Shawn & Merritt at Jack Lingo."
        path="/buy"
        jsonLd={[
          serviceSchema(
            'Real estate buyer\'s agent',
            'Buyer representation for primary homes, second homes, and investment properties across the Delaware beaches — Lewes, Rehoboth Beach, Dewey Beach, Bethany Beach, Fenwick Island, and Milton.',
          ),
          crumbs.jsonLd,
          faqSchema(BUYER_FAQ),
        ]}
      />

      {/* Hero */}
      <Section className="bg-neutral-charcoal pt-28 pb-16">
        {crumbs.node}
        <Reveal>
          <p className="font-display text-[12px] uppercase tracking-[0.3em] font-medium text-white/70">
            For Buyers
            <span className="block w-6 h-[2px] bg-accent-gold mt-3" aria-hidden="true" />
          </p>
          <h1 className="mt-4 font-display uppercase text-white leading-none text-[clamp(36px,5.5vw,72px)]">
            <span className="font-thin">Buy a Home at the </span>
            <span className="font-bold">Delaware Beaches</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-white/70 font-light leading-[1.9]">
            Whether it&apos;s a primary home in Lewes, a second home on the
            boardwalk side of Rehoboth, or a rental condo in Dewey — the right
            first step is understanding what kind of beach owner you want to be.
          </p>
        </Reveal>
      </Section>

      {/* Three paths */}
      <Section className="bg-primary-ink">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          <Reveal>
            <HomeIcon className="h-8 w-8 text-accent-gold mb-4" aria-hidden="true" />
            <h2 className="font-display text-lg font-bold uppercase tracking-wide text-white mb-3">
              Primary Home
            </h2>
            <p className="text-white/70 font-light leading-[1.9]">
              Living here year-round is different from vacationing here — school
              districts, winter life, and commute patterns matter as much as the
              beach. We&apos;ll show you the towns as they are in February, not
              just July, and help you choose between Lewes&apos;s walkability,
              Milton&apos;s value, and everything between.
            </p>
          </Reveal>
          <Reveal delay={100}>
            <Palmtree className="h-8 w-8 text-accent-gold mb-4" aria-hidden="true" />
            <h2 className="font-display text-lg font-bold uppercase tracking-wide text-white mb-3">
              Second Home
            </h2>
            <p className="text-white/70 font-light leading-[1.9]">
              The classic Delaware beach buy. Delaware&apos;s no-sales-tax,
              low-property-tax environment is built for second-home owners, and
              we&apos;ll help you weigh walkable in-town charm against newer
              amenity communities — plus the honest maintenance realities of
              owning near salt water.
            </p>
          </Reveal>
          <Reveal delay={200}>
            <KeyRound className="h-8 w-8 text-accent-gold mb-4" aria-hidden="true" />
            <h2 className="font-display text-lg font-bold uppercase tracking-wide text-white mb-3">
              Investment & Rental
            </h2>
            <p className="text-white/70 font-light leading-[1.9]">
              Beach rentals can perform well — but only where they&apos;re
              allowed and only at honest numbers. We underwrite with actual
              rental histories, flag every town license and HOA restriction
              before you offer, and tell you when a property&apos;s rental story
              doesn&apos;t pencil.
            </p>
          </Reveal>
        </div>
      </Section>

      {/* Delaware advantages */}
      <Section className="bg-neutral-charcoal">
        <div className="max-w-3xl">
          <Reveal>
            <h2 className="font-display uppercase text-white leading-none text-[clamp(28px,4vw,48px)] mb-8">
              <span className="font-thin">The </span>
              <span className="font-bold">Delaware Advantage</span>
            </h2>
          </Reveal>
          <div className="space-y-6 text-lg text-white/70 font-light leading-[1.9]">
            <p>
              Delaware&apos;s tax structure is a genuine, durable advantage for
              beach buyers: no sales tax, no tax on Social Security benefits,
              and property taxes in Sussex County that are among the lowest in
              the region. Second-home owners coming from New Jersey, Maryland,
              and Pennsylvania feel the difference every year — not just at
              closing.
            </p>
            <p>
              The one meaningful transaction cost is the realty transfer tax —
              4% of the sale price, customarily split between buyer and seller
              (verify the current rate and your negotiated split at contract
              time). We walk every buyer through a full closing-cost estimate
              before they offer, so nothing on the settlement sheet is a
              surprise.
            </p>
            <p>
              And because resort purchases have their own traps — flood zones,
              rental restrictions, condo association health, seasonal market
              swings — we build flood determinations, insurance quotes, and
              document review into our standard due diligence. Buying at the
              beach should be exciting, not naive.
            </p>
          </div>
        </div>
      </Section>

      {/* FAQ */}
      <Section className="bg-primary-ink">
        <div className="max-w-3xl">
          <Reveal>
            <h2 className="font-display uppercase text-white leading-none text-[clamp(28px,4vw,48px)] mb-8">
              <span className="font-thin">Buyer </span>
              <span className="font-bold">FAQs</span>
            </h2>
          </Reveal>
          <div className="space-y-3">
            {BUYER_FAQ.map((f) => (
              <details
                key={f.question}
                className="group border border-white/10 bg-white/5 open:border-accent-gold/40"
              >
                <summary className="cursor-pointer list-none p-5 font-display text-sm font-bold uppercase tracking-wide text-white flex items-center justify-between gap-4 [&::-webkit-details-marker]:hidden">
                  {f.question}
                  <span className="text-accent-gold text-xl leading-none transition-transform group-open:rotate-45" aria-hidden="true">
                    +
                  </span>
                </summary>
                <p className="px-5 pb-5 text-white/70 font-light leading-[1.9]">{f.answer}</p>
              </details>
            ))}
          </div>
          <p className="mt-8 text-white/60 font-light">
            More questions? See the full <Link to="/faq" className="text-accent-gold hover:underline">Delaware beaches FAQ</Link>,
            explore the <Link to="/listings" className="text-accent-gold hover:underline">live listings map</Link>,
            or <Link to="/contact" className="text-accent-gold hover:underline">reach out</Link> — no pressure, ever.
          </p>
        </div>
      </Section>

      {/* Town links */}
      <Section className="bg-neutral-charcoal">
        <Reveal>
          <h2 className="font-display uppercase text-white leading-none text-[clamp(24px,3vw,36px)] mb-8">
            <span className="font-thin">Where to Buy: </span>
            <span className="font-bold">Town-by-Town Guides</span>
          </h2>
        </Reveal>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {AREAS.map((a) => (
            <Link
              key={a.slug}
              to={`/areas/${a.slug}`}
              className="border border-white/10 bg-white/5 p-5 font-display text-sm font-bold uppercase tracking-wide text-white hover:border-accent-gold/50 transition-colors flex items-center justify-between gap-2"
            >
              {a.name}
              <ArrowRight className="h-4 w-4 text-accent-gold" aria-hidden="true" />
            </Link>
          ))}
        </div>
      </Section>

      {/* CTA */}
      <section className="bg-accent-gold py-16">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          <div>
            <h2 className="font-display uppercase text-primary-ink leading-none text-[clamp(26px,3.5vw,44px)]">
              <span className="font-thin">Ready to look at </span>
              <span className="font-bold">the beach?</span>
            </h2>
            <p className="mt-3 text-primary-ink/80 max-w-xl">
              Tell us what you&apos;re picturing — we&apos;ll tell you honestly
              which towns and streets fit, and send you what&apos;s actually
              available.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 shrink-0">
            <Link
              to="/contact?subject=BUYING"
              className="inline-flex items-center justify-center gap-2 bg-primary-ink text-white px-8 py-4 text-sm font-display font-bold uppercase tracking-[0.15em] hover:bg-neutral-charcoal transition-colors"
            >
              Start Your Search <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
            <a
              href="tel:+13025627954"
              className="inline-flex items-center justify-center gap-2 border-2 border-primary-ink text-primary-ink px-8 py-4 text-sm font-display font-bold uppercase tracking-[0.15em] hover:bg-primary-ink hover:text-white transition-colors"
            >
              <Phone className="h-4 w-4" aria-hidden="true" /> 302-562-7954
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}
